import { useEffect, useRef, useState } from "react";
import { MapPin, ArrowUpFromLine, Package, Minus, Plus, ChevronsUpDown, Check, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

export function ScanWizard({
  product,
  productLocations,
  locations,
  recentLocations = [],
  scanMode,
  inventoryMode,
  inventoryLocation,
  lagerplats,
  setLagerplats,
  antal,
  setAntal,
  onSubmit,
  onCancel,
  processing,
  locationInputRef,
}) {
  const [otherLocationOpen, setOtherLocationOpen] = useState(false);

  // Auto-set location in inventory mode
  useEffect(() => {
    if (inventoryMode && inventoryLocation) {
      setLagerplats(inventoryLocation);
    }
  }, [inventoryMode, inventoryLocation, setLagerplats]);

  // Auto-select location for scan-out if only one location with stock
  useEffect(() => {
    if (scanMode === "out" && productLocations.length > 0) {
      const locationsWithStock = productLocations.filter(loc => loc.antal > 0);
      if (locationsWithStock.length === 1) {
        setLagerplats(locationsWithStock[0].lagerplats);
      }
    }
  }, [scanMode, productLocations, setLagerplats]);

  // Get max quantity for out-scanning
  const getMaxQuantity = () => {
    if (scanMode !== "out" || !lagerplats) return null;
    const loc = productLocations.find(l => l.lagerplats === lagerplats);
    return loc?.antal || null;
  };

  const isOut = scanMode === "out";

  // Get locations where product exists with stock > 0
  const existingLocations = productLocations.filter(loc => loc.antal > 0);
  
  // Get other locations (not where product currently exists with stock)
  // This includes locations where stock is 0 (product was there before)
  const existingLocationNames = new Set(existingLocations.map(l => l.lagerplats));
  const otherLocations = locations.filter(loc => !existingLocationNames.has(loc));
  
  // Get recent locations that are not already shown (not where product exists)
  const recentLocationsFiltered = recentLocations.filter(
    loc => !existingLocationNames.has(loc) && locations.includes(loc)
  );

  const maxQty = getMaxQuantity();
  const canSubmit = lagerplats?.trim().length > 0 && antal > 0;

  const handleQuantityChange = (delta) => {
    const newVal = antal + delta;
    if (newVal < 1) return;
    if (maxQty && newVal > maxQty) return;
    setAntal(newVal);
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        {/* Product Header */}
        <div className={cn(
          "p-4 border-b",
          isOut ? "bg-red-500/10 border-red-500/20" : "bg-green-500/10 border-green-500/20"
        )}>
          <div className="flex items-center gap-3">
            {isOut ? (
              <ArrowUpFromLine className="h-5 w-5 text-red-500" />
            ) : (
              <Package className="h-5 w-5 text-green-500" />
            )}
            <div className="flex-1 min-w-0">
              <p className={cn(
                "font-bold truncate",
                isOut ? "text-red-500" : "text-green-500"
              )}>
                {inventoryMode ? "INVENTERA" : isOut ? "SKANNA UT" : "SKANNA IN"}
              </p>
              <p className="text-sm font-mono truncate">{product.artikelnummer || product.ean}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={onCancel} className="shrink-0">
              Avbryt
            </Button>
          </div>
        </div>

        {/* Combined Location + Quantity */}
        <div className="p-4 space-y-4">
          {/* Location Selection */}
          {!inventoryMode && (
            <div>
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2 block">
                <MapPin className="h-3 w-3 inline mr-1" />
                Lagerplats
              </Label>
              
              {/* Existing locations as buttons (for both scan-in and scan-out) */}
              {existingLocations.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-muted-foreground mb-2">Produkten finns på:</p>
                  <div className="flex flex-wrap gap-2">
                    {existingLocations.map(loc => (
                      <Button
                        key={loc.id}
                        variant={lagerplats === loc.lagerplats ? "default" : "outline"}
                        className={cn(
                          "gap-2",
                          lagerplats === loc.lagerplats && (isOut ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700")
                        )}
                        onClick={() => setLagerplats(loc.lagerplats)}
                        data-testid={`location-btn-${loc.lagerplats}`}
                      >
                        <MapPin className="h-4 w-4" />
                        {loc.lagerplats}
                        {loc.antal > 0 && (
                          <Badge variant="secondary" className="ml-1">
                            {loc.antal} st
                          </Badge>
                        )}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Recent locations as quick buttons (for scan-in only) */}
              {scanMode === "in" && recentLocationsFiltered.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Senast använda:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {recentLocationsFiltered.map(loc => (
                      <Button
                        key={`recent-${loc}`}
                        variant={lagerplats === loc ? "default" : "outline"}
                        className={cn(
                          "gap-2 border-dashed",
                          lagerplats === loc && "bg-green-600 hover:bg-green-700 border-solid"
                        )}
                        onClick={() => setLagerplats(loc)}
                        data-testid={`recent-location-btn-${loc}`}
                      >
                        <Clock className="h-4 w-4" />
                        {loc}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* For scan-in: Show dropdown for other locations */}
              {scanMode === "in" && (
                <Popover open={otherLocationOpen} onOpenChange={setOtherLocationOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      ref={locationInputRef}
                      variant="outline"
                      role="combobox"
                      aria-expanded={otherLocationOpen}
                      className={cn(
                        "w-full justify-between",
                        lagerplats && !existingLocationNames.has(lagerplats) && "border-green-500"
                      )}
                      data-testid="other-location-selector"
                    >
                      {lagerplats && !existingLocationNames.has(lagerplats) ? (
                        <span className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          {lagerplats}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Välj annan lagerplats...</span>
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
                    <Command>
                      <CommandInput placeholder="Sök lagerplats..." />
                      <CommandList>
                        <CommandEmpty>Ingen lagerplats hittad</CommandEmpty>
                        <CommandGroup className="max-h-64 overflow-y-auto">
                          {otherLocations.map((loc) => (
                            <CommandItem
                              key={loc}
                              value={loc}
                              onSelect={() => {
                                setLagerplats(loc);
                                setOtherLocationOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  lagerplats === loc ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                              {loc}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          )}

          {/* Quantity */}
          <div>
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2 block">
              Antal
              {maxQty && (
                <span className="text-muted-foreground ml-2">(max {maxQty})</span>
              )}
            </Label>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="icon"
                className="h-12 w-12 shrink-0"
                onClick={() => handleQuantityChange(-1)}
                disabled={antal <= 1}
              >
                <Minus className="h-5 w-5" />
              </Button>
              <Input
                type="number"
                value={antal}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 1;
                  if (val >= 1 && (!maxQty || val <= maxQty)) {
                    setAntal(val);
                  }
                }}
                className="text-center text-2xl font-bold h-12"
                min={1}
                max={maxQty || undefined}
                data-testid="wizard-qty-input"
              />
              <Button
                variant="outline"
                size="icon"
                className="h-12 w-12 shrink-0"
                onClick={() => handleQuantityChange(1)}
                disabled={maxQty && antal >= maxQty}
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Selected summary for inventory mode */}
          {inventoryMode && (
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Lagerplats</p>
              <Badge variant="outline" className="font-mono">
                <MapPin className="h-3 w-3 mr-1" />
                {lagerplats || inventoryLocation}
              </Badge>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="p-4 border-t bg-muted/30">
          <Button
            onClick={onSubmit}
            disabled={!canSubmit || processing}
            className={cn(
              "w-full h-14 text-lg",
              isOut 
                ? "bg-red-600 hover:bg-red-700" 
                : "bg-green-600 hover:bg-green-700"
            )}
            data-testid="scan-wizard-submit-btn"
          >
            {processing ? (
              "Registrerar..."
            ) : isOut ? (
              <>
                <ArrowUpFromLine className="h-5 w-5 mr-2" />
                Utleverans {antal} st
              </>
            ) : inventoryMode ? (
              <>
                <Package className="h-5 w-5 mr-2" />
                Inventera {antal} st
              </>
            ) : (
              <>
                <Package className="h-5 w-5 mr-2" />
                Inleverans {antal} st
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
